import { ReactNode, useState, useMemo, useCallback } from "react";
import { MentionTextareaProps } from "@jugl-web/domain-resources/common/components/RichTextarea";
import CanvasLoader from "../CanvasLoader";
import { AvailableTool, ToolOptions } from "../tools/Tool";
import type { ToolbarActionName } from "./Toolbar/ToolbarAction";
import { BRUSH_DEFAULT_OPTIONS } from "../tools/BrushTool";
import { TEXT_DEFAULT_OPTIONS } from "../tools/TextTool";
import { EditorContext } from "./EditorContext";

interface ComponentProps {
  children: ReactNode;
  onEdit: ({ file, caption }: { file?: File; caption?: string }) => void;
  onSave: () => void;
  closeModal: () => void;
  caption: string;
  mentions?: MentionTextareaProps["mentions"];
}

export const ImageEditorProvider = ({
  children,
  mentions,
  onEdit,
  onSave,
  closeModal,
  caption,
}: ComponentProps) => {
  const [instance, setInstance] = useState<CanvasLoader | null>(null);
  const [tool, setTool] = useState<ToolbarActionName>("preview");
  const [toolOptions, setToolOptions] = useState<ToolOptions | null>(null);

  const setActiveTool = useCallback(
    (toolName: ToolbarActionName, toolInstance: AvailableTool) => {
      instance?.setActiveTool(toolInstance);

      setTool(toolName);

      switch (toolName) {
        case "preview":
          setToolOptions(null);
          break;
        case "brush":
          setToolOptions(BRUSH_DEFAULT_OPTIONS);
          break;
        case "text":
          setToolOptions(TEXT_DEFAULT_OPTIONS);
          break;
        default:
          break;
      }
    },
    [instance]
  );

  const setActiveToolOptions = useCallback(
    (options: ToolOptions) => {
      setToolOptions((prev: ToolOptions | null) => ({
        ...prev,
        ...options,
      }));

      instance?.updateToolOptions(options);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [instance, tool]
  );

  const value = useMemo(
    () => ({
      instance,
      setInstance,
      setActiveTool,
      activeTool: tool,
      toolOptions,
      setToolOptions: setActiveToolOptions,
      onEdit,
      onSave,
      closeModal,
      caption,
      mentions,
    }),
    [
      instance,
      setInstance,
      setActiveTool,
      tool,
      toolOptions,
      mentions,
      setActiveToolOptions,
      closeModal,
      caption,
      onEdit,
      onSave,
    ]
  );

  return (
    <EditorContext.Provider value={value}>{children}</EditorContext.Provider>
  );
};
